import React, { createContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, MessageCircle, ArrowLeft } from "lucide-react";
import { BrandLogo } from "./BrandLogos";

export const CaseStudyContext = createContext(() => {});

const EASE = [0.22, 0.61, 0.24, 1];

export const PROJECTS = [
  {
    id: "autoroble", index: "01 / 06", name: "Auto Roble", handle: "@autoroble",
    sector: "Automotive · Service & Parts", year: "2025",
    logoW: "max-w-[300px]",
    palette: { bg: "#9B1B1B", text: "#F4F1EB", accent: "#F4F1EB" },
    brief: "Auto Roble sold real quality but showed up online like a parts catalogue. We rebuilt the identity around one promise, “Creciendo con calidad,” and turned the feed into a confident, high-contrast garage voice that earns trust before the first visit.",
    services: ["Identity", "Social", "Campaigns"],
    results: [["+180%", "Profile visits"], ["3.4x", "Weekly reach"], ["+62%", "Quote requests"], ["24", "Reels / month"]],
    feed: [
      ["“Your car, growing with quality.” Brand relaunch post.", "#AutoRoble #CalidadTotal", "1.2k", "84"],
      ["Before / after brake service: trust in six seconds.", "#ServicioAuto", "940", "51"],
      ["Meet the team keeping the city moving.", "#CreciendoConCalidad", "1.6k", "120"],
    ],
  },
  {
    id: "tacosfridamtl", index: "02 / 06", name: "Tacos Frida MTL", handle: "@tacosfridamtl",
    sector: "Mexican Taquería · St-Henri, MTL", year: "2025",
    logoW: "max-w-[300px]",
    palette: { bg: "#FF2E88", text: "#FFFFFF", accent: "#FFD23F" },
    brief: "Tacos Frida serves authentic Oaxacan tacos on Notre-Dame in St-Henri. We leaned into colour, hand-lettering and lucha-libre energy so every post feels like a fiesta plated up: warm, loud and impossible to scroll past.",
    services: ["Lettering", "Menu Drops", "Content"],
    results: [["+240%", "Engagement"], ["5.1x", "Story views"], ["+38%", "Weekend covers"], ["18", "Menu drops"]],
    feed: [
      ["Al pastor straight off the trompo, today only.", "#TacosFridaMTL #StHenri", "2.3k", "210"],
      ["Birria & consomé: dip, don’t think.", "#Birria #MTLeats", "1.8k", "96"],
      ["Cochinita, carnitas, chorizo con papa: pick three.", "#Oaxaca #NotreDameO", "1.1k", "44"],
    ],
  },
  {
    id: "distriroble", index: "03 / 06", name: "Distri Roble", handle: "@distriroble",
    sector: "Hyundai Dealership · Montería, CO", year: "2025",
    logoW: "max-w-[280px]",
    palette: { bg: "#17294F", text: "#F4F1EB", accent: "#7FA8D9" },
    brief: "Distri Roble is the official Hyundai dealer in Montería. Around the promise “Más que una entrega,” we turned the feed into a polished showroom: model launches, test-drive invites and delivery-day moments that feel like an event.",
    services: ["Social", "Launches", "Content"],
    results: [["+120%", "Showroom visits"], ["3.2x", "Reel reach"], ["+58%", "Test-drive leads"], ["16", "Launch posts"]],
    feed: [
      ["La nueva Kona ya está aquí: agenda tu test drive.", "#MiHyundai #Kona", "1.1k", "63"],
      ["Día de entrega: más que una entrega.", "#Hyundai #Montería", "1.4k", "88"],
      ["Santa Fe & Tucson híbridas: potencia sin culpa.", "#Hyundai #Híbrida", "920", "41"],
    ],
  },
  {
    id: "viniltv", index: "04 / 06", name: "Vinil TV", handle: "@viniltv",
    sector: "Music · Media · Broadcast", year: "2025",
    logoW: "max-w-[320px]",
    palette: { bg: "#0E1116", text: "#1CA0E3", accent: "#F5821F" },
    brief: "Vinil TV is culture on repeat. We built a loud neon-and-broadcast voice: heavy type, motion cues and a channel that always feels on air, so every drop lands like appointment viewing.",
    services: ["Brand", "Motion", "Series"],
    results: [["+320%", "Views"], ["6.4x", "Shares"], ["+120%", "Followers"], ["30", "Episodes"]],
    feed: [
      ["Episode 07 drops tonight: press play.", "#VinilTV #OnAir", "5.2k", "340"],
      ["Crate-digging with this week’s guest.", "#VinylCulture", "3.9k", "180"],
      ["REC light’s on. New season loading.", "#PressPlay", "4.4k", "260"],
    ],
  },
  {
    id: "ora", index: "05 / 06", name: "ORA Boutique", handle: "ORA Boutique",
    sector: "Fashion Boutique", year: "2025",
    logoW: "max-w-[300px]",
    palette: { bg: "#F4F1EB", text: "#141414", accent: "#B4892B" },
    brief: "ORA Boutique dresses the season with restraint. A refined serif wordmark, a botanical accent and a gold-lit feed built for timeless, editorial calm: luxury that whispers instead of shouts.",
    services: ["Logo", "Editorial", "Feed Art"],
    results: [["+150%", "Saves"], ["3.1x", "Lookbook reach"], ["+55%", "DM enquiries"], ["8", "Collections"]],
    feed: [
      ["“Wear the season.” Fall lookbook is here.", "#ORABoutique #FW25", "1.9k", "88"],
      ["The gold-hour edit: five timeless pieces.", "#SlowFashion", "2.2k", "104"],
      ["In-store styling, by appointment.", "#Boutique", "1.3k", "37"],
    ],
  },
  {
    id: "emporium", index: "06 / 06", name: "Montréal Emporium", handle: "Montréal Emporium",
    sector: "Luxury Retail · Editorial", year: "2025",
    logoW: "max-w-[320px]",
    palette: { bg: "#F4F1EB", text: "#9B1B1B", accent: "#9B1B1B" },
    brief: "Montréal Emporium is the art of the acquired. A heritage archway mark and high-fashion Bodoni contrast give a curated house a near-monochrome editorial system: old-world confidence, city-fresh.",
    services: ["Logo", "Art Direction"],
    results: [["+130%", "Profile reach"], ["2.9x", "Feature saves"], ["+48%", "Waitlist signups"], ["6", "Drops"]],
    feed: [
      ["Under the arches: the acquired collection.", "#MontrealEmporium", "1.4k", "62"],
      ["A quiet-luxury drop, numbered and limited.", "#Curated", "1.7k", "75"],
      ["Heritage, reissued for the city.", "#Editorial", "1.1k", "29"],
    ],
  },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const CaseStudyOverlay = ({ project, onClose }) => (
  <AnimatePresence>
    {project && (
      <motion.div
        key={project.id}
        className="fixed inset-0 z-[10001] overflow-y-auto no-bar"
        style={{ background: project.palette.bg, color: project.palette.text }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, filter: "blur(10px)" }}
        transition={{ duration: 0.5, ease: EASE }}
        data-testid={`case-${project.id}`}
      >
        <div className="mx-auto max-w-6xl px-6 sm:px-12 lg:px-16 py-10 sm:py-16">
          {/* top bar */}
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              data-testid="case-back"
              className="group flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.25em] opacity-70 hover:opacity-100 transition-opacity"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Back to poster
            </button>
            <button onClick={onClose} data-testid="case-close" aria-label="Close" className="hover:rotate-90 transition-transform duration-300">
              <X size={26} />
            </button>
          </div>

          {/* header */}
          <div className="mt-10 sm:mt-16 flex items-center justify-between font-mono2 text-xs uppercase tracking-[0.28em] opacity-80">
            <span>{project.index}</span>
            <span>{project.handle}</span>
          </div>
          <Reveal className="mt-8 sm:mt-12" delay={0.1}>
            <BrandLogo brand={project.id} className={`w-full ${project.logoW}`} />
          </Reveal>
          <Reveal delay={0.2} className="mt-6 font-mono2 text-xs uppercase tracking-[0.25em]">
            <span style={{ color: project.palette.accent }}>{project.sector}</span>
            <span className="opacity-60"> &middot; {project.year}</span>
          </Reveal>

          {/* brief */}
          <div className="mt-14 sm:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Reveal delay={0.15} className="lg:col-span-3">
              <h3 className="font-mono2 text-xs uppercase tracking-[0.3em]" style={{ color: project.palette.accent }}>§ The Brief</h3>
            </Reveal>
            <Reveal delay={0.25} className="lg:col-span-9">
              <p className="font-corm font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.12] tracking-tight">{project.brief}</p>
            </Reveal>
          </div>

          {/* results */}
          <div className="mt-16 sm:mt-24">
            <Reveal className="font-mono2 text-xs uppercase tracking-[0.3em] mb-8" delay={0.1}>
              <span style={{ color: project.palette.accent }}>§ Results</span>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {project.results.map(([v, l], i) => (
                <Reveal key={l} delay={0.15 + i * 0.08}>
                  <div className="border-t pt-4" style={{ borderColor: project.palette.text + "33" }}>
                    <div className="font-corm font-light leading-none text-5xl sm:text-6xl" style={{ color: project.palette.accent }}>{v}</div>
                    <div className="mt-3 font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-70">{l}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* feed samples */}
          <div className="mt-16 sm:mt-24">
            <Reveal className="font-mono2 text-xs uppercase tracking-[0.3em] mb-8" delay={0.1}>
              <span style={{ color: project.palette.accent }}>§ From the feed</span>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {project.feed.map(([cap, tag, likes, comments], i) => (
                <Reveal key={i} delay={0.15 + i * 0.1}>
                  <div
                    className="h-full rounded-sm p-6 flex flex-col justify-between aspect-square"
                    style={{ background: project.palette.accent + "14", border: `1px solid ${project.palette.text}22` }}
                  >
                    <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] opacity-50">Post {String(i + 1).padStart(2, "0")}</div>
                    <p className="font-corm text-2xl sm:text-[1.7rem] leading-tight">{cap}</p>
                    <div>
                      <p className="font-mono2 text-xs" style={{ color: project.palette.accent }}>{tag}</p>
                      <div className="mt-3 flex items-center gap-5 font-mono2 text-xs opacity-70">
                        <span className="flex items-center gap-1.5"><Heart size={13} /> {likes}</span>
                        <span className="flex items-center gap-1.5"><MessageCircle size={13} /> {comments}</span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* footer */}
          <div className="mt-16 sm:mt-20 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t pt-8" style={{ borderColor: project.palette.text + "33" }}>
            <div className="flex flex-wrap gap-2">
              {project.services.map((s) => (
                <span key={s} className="font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.25em] border px-3 py-1 rounded-full" style={{ borderColor: project.palette.text }}>{s}</span>
              ))}
            </div>
            <button onClick={onClose} data-testid="case-close-bottom" className="font-mono2 text-xs uppercase tracking-[0.25em] opacity-70 hover:opacity-100 transition-opacity self-start sm:self-auto">
              Close ✕
            </button>
          </div>
          <p className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.2em] opacity-40">* Representative campaign outcomes.</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
